import { useMemo, useState } from "react";

import { Combobox, DropdownButton } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { FormFieldLabel } from "app/pages/sales-intelligence/common-components/form-fields";
import { StyledBaseDropdownButtonWrap } from "app/pages/sales-intelligence/common-components/styles";
import { EMPTY_ARRAY } from "app/pages/sales-intelligence/constants/common";
import { createMultiselectLabel } from "app/pages/sales-intelligence/utils/dropdowns";

import type { IFilterViewBaseModelProps } from "../../../types/filterInstance";
import type { DynamicComboboxFilterOption, DynamicComboboxFilterOptions } from "../../../types/filterOptions";
import { resolveOptionLabel } from "../../../utils/filterOptions";
import { isDynamicListSearchActive } from "../../../utils/search";

import SearchableComboboxContent from "./SearchableComboboxContent";

const EnumArrayComboboxFilter = ({
    filter: {
        options: { options, label, placeholder },
    },
    value = EMPTY_ARRAY,
    disabledOptionIds,
    isTopLayer,
    onFilterUpdate,
}: IFilterViewBaseModelProps<readonly string[], DynamicComboboxFilterOptions>) => {
    const [isOpen, setIsOpen] = useState(false);

    const isOptionDisabled = (id: string): boolean => !!disabledOptionIds?.includes(id) && !value.includes(id);

    const selectedLabels = useMemo(() => value.map((id) => resolveOptionLabel(options, id)), [value, options]);

    const handleToggle = (id: string) => {
        const next = value.includes(id) ? value.filter((selectedId) => selectedId !== id) : [...value, id];

        onFilterUpdate(next);
    };

    const renderOption = (option: DynamicComboboxFilterOption) => (
        <Combobox.Option
            key={option.id}
            id={option.id}
            textValue={option.label}
            isDisabled={isOptionDisabled(option.id)}
            onClick={() => !isOptionDisabled(option.id) && handleToggle(option.id)}
        >
            <Flex
                alignItems="center"
                justifyContent="space-between"
                gap="8px"
                width="100%"
            >
                <Combobox.OptionLabel>{option.label}</Combobox.OptionLabel>
                <Combobox.OptionCheckbox isChecked={value.includes(option.id)} />
            </Flex>
        </Combobox.Option>
    );

    const combobox = (
        <Combobox
            data-automation="enum-array-combobox-filter"
            placement="bottom-start"
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            {...(isTopLayer ? { "data-react-aria-top-layer": "true" } : {})}
        >
            <Combobox.Trigger>
                <StyledBaseDropdownButtonWrap>
                    <DropdownButton hasValue={value?.length > 0}>
                        {createMultiselectLabel(selectedLabels, placeholder)}
                    </DropdownButton>
                </StyledBaseDropdownButtonWrap>
            </Combobox.Trigger>

            {isDynamicListSearchActive(options) ? (
                <SearchableComboboxContent
                    isOpen={isOpen}
                    options={options}
                    searchDataAutomation="enum-array-combobox-filter-search"
                    renderOption={renderOption}
                />
            ) : (
                <Combobox.Content>{options.map(renderOption)}</Combobox.Content>
            )}
        </Combobox>
    );

    return label ? <FormFieldLabel text={label}>{combobox}</FormFieldLabel> : combobox;
};

export default EnumArrayComboboxFilter;
